import React, { useState } from 'react';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Slider } from '../../ui/slider';
import { Button } from '../../ui/button';
import { Settings2, ChevronDown, Tag, AlignLeft, AlignCenter, AlignRight, Square, X, Clock } from 'lucide-react';

const QUICK_PLACEHOLDERS = [
  { key: '{{price}}', label: 'Price' },
  { key: '{{address}}', label: 'Full Address' },
  { key: '{{street}}', label: 'Street' },
  { key: '{{city}}', label: 'City' },
  { key: '{{state}}', label: 'State' },
  { key: '{{zip}}', label: 'Zip' },
  { key: '{{city_state}}', label: 'City, State' },
  { key: '{{beds}}', label: 'Beds' },
  { key: '{{baths}}', label: 'Baths' },
  { key: '{{sqft}}', label: 'Sq Ft' },
  { key: '{{agent_name}}', label: 'Agent' },
  { key: '{{agent_phone}}', label: 'Phone' },
  { key: '{{open_house_date}}', label: 'Open House' },
  { key: '{{mls_number}}', label: 'MLS #' },
  { key: '{{property_type}}', label: 'Type' },
];

const ALIGN_OPTIONS = [
  { value: 'left', icon: AlignLeft },
  { value: 'center', icon: AlignCenter },
  { value: 'right', icon: AlignRight },
];

export const PropertyInspector = ({ layer, onUpdate }) => {
  const [showPH, setShowPH] = useState(false);
  const [showShadow, setShowShadow] = useState(false);
  const [showBgHighlight, setShowBgHighlight] = useState(false);

  if (!layer) {
    return (
      <div className="border border-border/50 rounded-lg bg-card p-3" data-testid="property-inspector">
        <h3 className="text-sm font-semibold flex items-center gap-1.5 mb-2">
          <Settings2 className="h-4 w-4" /> Properties
        </h3>
        <p className="text-xs text-muted-foreground text-center py-4">Select a layer to edit</p>
      </div>
    );
  }

  const u = (field, value) => onUpdate(layer.id, { [field]: value });

  const insertPlaceholder = (ph) => {
    const current = layer.content || '';
    u('content', current + ph);
    setShowPH(false);
  };

  const shadow = layer.shadow || {};
  const bgHighlight = layer.bgHighlight || {};

  const updateShadow = (key, val) => {
    u('shadow', { ...shadow, [key]: val });
  };

  const updateBgHighlight = (key, val) => {
    u('bgHighlight', { ...bgHighlight, [key]: val });
  };

  return (
    <div className="border border-border/50 rounded-lg bg-card p-3 space-y-3" data-testid="property-inspector">
      <h3 className="text-sm font-semibold flex items-center gap-1.5">
        <Settings2 className="h-4 w-4" /> Properties
        <span className="text-xs font-normal text-muted-foreground capitalize">({layer.type})</span>
      </h3>

      {/* Timing — prominent at top */}
      <div className="bg-violet-500/10 border border-violet-500/20 rounded-md p-2.5" data-testid="timing-control">
        <div className="flex items-center gap-1.5 mb-1.5">
          <Clock className="h-3.5 w-3.5 text-violet-400" />
          <Label className="text-xs font-medium text-violet-300">Visibility Timing</Label>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-[10px] text-muted-foreground">Start (sec)</Label>
            <Input type="number" value={layer.start ?? 0} onChange={(e) => u('start', Number(e.target.value))} className="h-7 text-xs" min={0} step={0.5} data-testid="layer-start" />
          </div>
          <div>
            <Label className="text-[10px] text-muted-foreground">End (sec)</Label>
            <Input type="number" value={layer.end >= 999 ? '' : layer.end} placeholder="End of reel" onChange={(e) => u('end', e.target.value === '' ? 999 : Number(e.target.value))} className="h-7 text-xs" min={0} step={0.5} data-testid="layer-end" />
          </div>
        </div>
        <p className="text-[9px] text-muted-foreground mt-1">Shows from {layer.start ?? 0}s to {layer.end >= 999 ? 'end' : `${layer.end}s`}</p>
      </div>

      {/* Opacity */}
      <div data-testid="opacity-control">
        <Label className="text-xs text-muted-foreground">Opacity: {Math.round(layer.opacity * 100)}%</Label>
        <Slider value={[layer.opacity * 100]} onValueChange={([val]) => u('opacity', val / 100)} max={100} step={1} />
      </div>

      {/* Position */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs text-muted-foreground">X</Label>
          <Input type="number" value={Math.round(layer.x)} onChange={(e) => u('x', Number(e.target.value))} className="h-7 text-xs" data-testid="layer-x" />
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Y</Label>
          <Input type="number" value={Math.round(layer.y)} onChange={(e) => u('y', Number(e.target.value))} className="h-7 text-xs" data-testid="layer-y" />
        </div>
      </div>

      {/* Size */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs text-muted-foreground">Width</Label>
          <Input type="number" value={Math.round(layer.width)} onChange={(e) => u('width', Number(e.target.value))} className="h-7 text-xs" data-testid="layer-width" />
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Height</Label>
          <Input type="number" value={Math.round(layer.height)} onChange={(e) => u('height', Number(e.target.value))} className="h-7 text-xs" data-testid="layer-height" />
        </div>
      </div>

      {/* Rotation */}
      <div>
        <Label className="text-xs text-muted-foreground">Rotation: {layer.rotation}deg</Label>
        <Slider value={[layer.rotation]} onValueChange={([val]) => u('rotation', val)} min={-180} max={180} step={1} />
      </div>

      {/* ══════ TEXT-SPECIFIC ══════ */}
      {layer.type === 'text' && (
        <div className="space-y-3 pt-2 border-t border-border/30">

          {/* Text Content (multi-line textarea) */}
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs text-muted-foreground">Text</Label>
              <Button variant="ghost" size="sm" className="h-5 text-[10px] gap-0.5 px-1"
                onClick={() => setShowPH(!showPH)} data-testid="insert-placeholder-btn">
                <Tag className="h-3 w-3" /> Insert Field <ChevronDown className="h-2.5 w-2.5" />
              </Button>
            </div>
            <textarea
              value={layer.content || ''}
              onChange={(e) => u('content', e.target.value)}
              className="w-full text-xs bg-background border border-border rounded-md px-2 py-1.5 min-h-[60px] resize-y focus:outline-none focus:ring-1 focus:ring-ring"
              placeholder="Enter text... (press Enter for new line)"
              data-testid="layer-content"
            />
            {showPH && (
              <div className="flex flex-wrap gap-1 mt-1 p-1.5 bg-muted/30 rounded" data-testid="placeholder-dropdown">
                {QUICK_PLACEHOLDERS.map(ph => (
                  <button key={ph.key} className="text-[9px] px-1.5 py-0.5 rounded bg-purple-500/10 text-purple-500 hover:bg-purple-500/20 transition-colors"
                    onClick={() => insertPlaceholder(ph.key)} data-testid={`insert-${ph.key.replace(/[{}]/g, '')}`}>
                    {ph.label}
                  </button>
                ))}
              </div>
            )}
            {layer.content && /\{\{.+\}\}/.test(layer.content) && (
              <p className="text-[9px] text-purple-400 mt-0.5">Contains placeholder fields</p>
            )}
          </div>

          {/* Font Size + Color */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs text-muted-foreground">Font Size</Label>
              <Input type="number" value={layer.fontSize || 48} onChange={(e) => u('fontSize', Number(e.target.value))} className="h-7 text-xs" min={8} max={200} />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Color</Label>
              <Input type="color" value={layer.fontColor?.startsWith('#') ? layer.fontColor : '#ffffff'} onChange={(e) => u('fontColor', e.target.value)} className="h-7 w-full" />
            </div>
          </div>

          {/* Text Alignment */}
          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">Alignment</Label>
            <div className="flex gap-1" data-testid="text-align-group">
              {ALIGN_OPTIONS.map(({ value, icon: Icon }) => (
                <Button key={value} variant={layer.textAlign === value ? 'default' : 'outline'} size="sm"
                  className={`h-7 w-9 p-0 ${layer.textAlign === value ? 'bg-violet-600 hover:bg-violet-500' : ''}`}
                  onClick={() => u('textAlign', value)} data-testid={`text-align-${value}`}>
                  <Icon className="h-3.5 w-3.5" />
                </Button>
              ))}
            </div>
          </div>

          {/* Line Height */}
          <div>
            <Label className="text-xs text-muted-foreground">Line Height: {(layer.lineHeight || 1.2).toFixed(1)}</Label>
            <Slider value={[(layer.lineHeight || 1.2) * 10]} onValueChange={([val]) => u('lineHeight', val / 10)} min={8} max={30} step={1} />
          </div>

          {/* ── Text Shadow ── */}
          <div className="border border-border/20 rounded-md p-2">
            <button className="flex items-center justify-between w-full text-xs font-medium text-slate-300"
              onClick={() => setShowShadow(!showShadow)} data-testid="toggle-shadow">
              Text Shadow {shadow.color ? <span className="w-3 h-3 rounded-full border border-white/20" style={{ backgroundColor: shadow.color }} /> : null}
              <ChevronDown className={`h-3 w-3 transition-transform ${showShadow ? 'rotate-180' : ''}`} />
            </button>
            {showShadow && (
              <div className="space-y-2 mt-2">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[10px] text-muted-foreground">Color</Label>
                    <Input type="color" value={shadow.color || '#000000'} onChange={(e) => updateShadow('color', e.target.value)} className="h-7 w-full" data-testid="shadow-color" />
                  </div>
                  <div>
                    <Label className="text-[10px] text-muted-foreground">Blur: {shadow.blur || 0}px</Label>
                    <Slider value={[shadow.blur || 0]} onValueChange={([val]) => updateShadow('blur', val)} max={20} step={1} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[10px] text-muted-foreground">Offset X: {shadow.offsetX || 0}</Label>
                    <Slider value={[shadow.offsetX || 0]} onValueChange={([val]) => updateShadow('offsetX', val)} min={-20} max={20} step={1} />
                  </div>
                  <div>
                    <Label className="text-[10px] text-muted-foreground">Offset Y: {shadow.offsetY || 0}</Label>
                    <Slider value={[shadow.offsetY || 0]} onValueChange={([val]) => updateShadow('offsetY', val)} min={-20} max={20} step={1} />
                  </div>
                </div>
                {shadow.color && (
                  <Button variant="ghost" size="sm" className="h-6 text-[10px] text-red-400 w-full"
                    onClick={() => u('shadow', {})} data-testid="clear-shadow">Clear Shadow</Button>
                )}
              </div>
            )}
          </div>

          {/* ── Background Highlight ── */}
          <div className="border border-border/20 rounded-md p-2">
            <button className="flex items-center justify-between w-full text-xs font-medium text-slate-300"
              onClick={() => setShowBgHighlight(!showBgHighlight)} data-testid="toggle-bg-highlight">
              <span className="flex items-center gap-1.5">
                <Square className="h-3 w-3" /> Background Highlight
              </span>
              <ChevronDown className={`h-3 w-3 transition-transform ${showBgHighlight ? 'rotate-180' : ''}`} />
            </button>
            {showBgHighlight && (
              <div className="space-y-2 mt-2">
                <div className="flex items-center gap-2">
                  <input type="checkbox" checked={bgHighlight.enabled || false}
                    onChange={(e) => updateBgHighlight('enabled', e.target.checked)}
                    className="rounded" data-testid="bg-highlight-toggle" />
                  <Label className="text-[10px] text-muted-foreground">Enable background box</Label>
                </div>
                {bgHighlight.enabled && (
                  <>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-[10px] text-muted-foreground">Color</Label>
                        <Input type="color" value={bgHighlight.color || '#000000'}
                          onChange={(e) => updateBgHighlight('color', e.target.value)}
                          className="h-7 w-full" data-testid="bg-highlight-color" />
                      </div>
                      <div>
                        <Label className="text-[10px] text-muted-foreground">Opacity: {Math.round((bgHighlight.opacity ?? 0.6) * 100)}%</Label>
                        <Slider value={[(bgHighlight.opacity ?? 0.6) * 100]}
                          onValueChange={([val]) => updateBgHighlight('opacity', val / 100)}
                          max={100} step={5} />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-[10px] text-muted-foreground">Padding: {bgHighlight.padding || 12}px</Label>
                        <Slider value={[bgHighlight.padding || 12]}
                          onValueChange={([val]) => updateBgHighlight('padding', val)}
                          max={40} step={2} />
                      </div>
                      <div>
                        <Label className="text-[10px] text-muted-foreground">Radius: {bgHighlight.borderRadius || 0}px</Label>
                        <Slider value={[bgHighlight.borderRadius || 0]}
                          onValueChange={([val]) => updateBgHighlight('borderRadius', val)}
                          max={24} step={2} />
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Legacy bgColor (hidden when bgHighlight is used) */}
          {!bgHighlight.enabled && (
            <div>
              <Label className="text-xs text-muted-foreground">Quick Background</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input type="color" value={layer._bgPickColor || '#000000'}
                  onChange={(e) => {
                    const hex = e.target.value;
                    const op = layer._bgPickOpacity ?? 0.5;
                    u('_bgPickColor', hex);
                    u('bgColor', `${hex}@${op.toFixed(2)}`);
                  }}
                  className="h-7 w-12 shrink-0" data-testid="legacy-bg-color" />
                <div className="flex-1">
                  <Label className="text-[10px] text-muted-foreground">Opacity: {Math.round((layer._bgPickOpacity ?? 0.5) * 100)}%</Label>
                  <Slider value={[(layer._bgPickOpacity ?? 0.5) * 100]}
                    onValueChange={([val]) => {
                      const op = val / 100;
                      const hex = layer._bgPickColor || '#000000';
                      u('_bgPickOpacity', op);
                      u('bgColor', op > 0 ? `${hex}@${op.toFixed(2)}` : '');
                    }}
                    max={100} step={5} />
                </div>
                {layer.bgColor && (
                  <Button variant="ghost" size="icon" className="h-6 w-6 text-red-400 shrink-0"
                    onClick={() => { u('bgColor', ''); u('_bgPickOpacity', 0); }} data-testid="clear-legacy-bg">
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Shape-specific */}
      {(layer.type === 'rect' || layer.type === 'circle') && (
        <div className="space-y-2 pt-2 border-t border-border/30">
          <div>
            <Label className="text-xs text-muted-foreground">Fill Color</Label>
            <div className="flex gap-2">
              <Input type="color" value={layer.fill?.startsWith('#') ? layer.fill : '#8B5CF6'} onChange={(e) => u('fill', e.target.value)} className="h-7 w-12" />
              <Input value={layer.fill || ''} onChange={(e) => u('fill', e.target.value)} className="h-7 text-xs flex-1" data-testid="layer-fill" />
            </div>
          </div>
          {layer.type === 'rect' && (
            <>
              <div>
                <Label className="text-xs text-muted-foreground">Border Radius: {layer.borderRadius || 0}px</Label>
                <Slider value={[layer.borderRadius || 0]} onValueChange={([val]) => u('borderRadius', val)} max={100} step={1} data-testid="border-radius-slider" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs text-muted-foreground">Stroke Color</Label>
                  <Input type="color" value={layer.stroke?.startsWith('#') ? layer.stroke : '#ffffff'} onChange={(e) => u('stroke', e.target.value)} className="h-7 w-full" data-testid="stroke-color" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Stroke Width: {layer.strokeWidth || 0}px</Label>
                  <Slider value={[layer.strokeWidth || 0]} onValueChange={([val]) => u('strokeWidth', val)} max={20} step={1} data-testid="stroke-width" />
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};
